import React from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Id, Identifiable, Role, SecondaryCriteria } from "@planzone/dto";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
  useSaveTimeRecord,
} from "@planzone/view-models";
import { useRoles } from "@scenes/safeguard";
import { Modal } from "@common/modal";
import { Button } from "@common/button";
import { NumberInput } from "@common/numberInput";
import { TextInput } from "@common/textInput";
import { Language } from "@common/language";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { Props } from "./types";
import styles from "./styles.scss";

function hasRole(role: Identifiable<Role>, roles: Id[]) {
  return !!roles.find((id) => id === role.uuid || id === role.name);
}

function isReadable(
  roles: Identifiable<Role>[],
  criteria: Identifiable<SecondaryCriteria>,
) {
  return !!roles.find((role) => hasRole(role, criteria.accessRoles.read));
}

function TimeRecordCriteria({ timeRecord, isDisabled, close }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "timeRecord",
  );
  const { isRequestPending, saveTimeRecord } = useSaveTimeRecord(timeRecord);
  const roles = useRoles({});
  const [timeSpent, setTimeSpent] = React.useState(
    timeRecord.timeSpent > 0 || isDisabled ? timeRecord.timeSpent : 1,
  );
  const [attributes, setAttributes] = React.useState(
    timeRecord.attributes || {},
  );
  const isTimeSpentValid =
    !isNaN(timeSpent) && timeSpent >= 0 && timeSpent <= 24;
  const criteriaList = template.filter((criteria) =>
    isReadable(roles, criteria),
  );
  const date = DateTime.fromObject({
    year: timeRecord.year,
    month: timeRecord.month,
    day: timeRecord.date,
  });

  const save = () => {
    saveTimeRecord({
      timeRecord: { ...timeRecord, timeSpent, attributes },
    });
    close();
  };

  return (
    <Modal header={t("timeRecordCriteria.title")} close={close}>
      <div className={styles.timeRecordCriteria}>
        <div className={styles.main}>
          <div className={styles.field}>
            <p className={styles.fieldLabel}>{t("timeRecordCriteria.task")}</p>
            <TextInput
              placeholder=""
              value={timeRecord.task.name}
              isDisabled
              input={() => {}}
            />
          </div>
          <div className={styles.field}>
            <p className={styles.fieldLabel}>
              {t("timeRecordCriteria.resource")}
            </p>
            <TextInput
              placeholder=""
              value={timeRecord.resource.name}
              isDisabled
              input={() => {}}
            />
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <p className={styles.fieldLabel}>
                {t("timeRecordCriteria.date")}
              </p>
              <TextInput
                placeholder=""
                value={date.toLocaleString(DateTime.DATE_SHORT)}
                isDisabled
                input={() => {}}
              />
            </div>
            <div className={styles.field}>
              <p className={styles.fieldLabel}>
                {t("timeRecordCriteria.timeSpent")}
              </p>
              <NumberInput
                className={styles.numberInput}
                value={timeSpent}
                min={0}
                max={24}
                step={0.25}
                autoFocus={!isDisabled}
                isDisabled={isDisabled || isRequestPending}
                input={(value) => setTimeSpent(value)}
              />
            </div>
          </div>
          {criteriaList.length > 0 && (
            <div className={styles.criteriaSection}>
              <p className={styles.sectionTitle}>
                {t("timeRecordCriteria.criteria")}
              </p>
              <Language>
                {({ currentLanguage }) => (
                  <div className={styles.criteriaList}>
                    {criteriaList.map((criteria) => (
                      <div key={criteria.uuid} className={styles.field}>
                        <p className={styles.fieldLabel}>
                          {criteria.labels[
                            currentLanguage as keyof typeof criteria.labels
                          ] || criteria.name}
                        </p>
                        <SecondaryCriteriaForm
                          criteria={criteria}
                          value={attributes[criteria.uuid] || ""}
                          flexible
                          disabled={
                            isDisabled || isRequestPending || !(timeSpent > 0)
                          }
                          submit={(value) =>
                            setAttributes((current) => ({
                              ...current,
                              [criteria.uuid]: value,
                            }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}
              </Language>
            </div>
          )}
        </div>
        <div className={styles.buttons}>
          <Button type="cancel" press={close}>
            {t(
              isDisabled
                ? "timeRecordCriteria.close"
                : "timeRecordCriteria.cancel",
            )}
          </Button>
          {!isDisabled && (
            <Button
              type="submit"
              isDisabled={!isTimeSpentValid || isRequestPending}
              press={save}
            >
              {t("timeRecordCriteria.save")}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export { TimeRecordCriteria };
